import React from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../components/useReveal';
import { useLang } from '../context/LanguageContext';
import t from '../translations';
import './Home.css';

/* ── Small reusable icon SVGs ── */
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
  </svg>
);
const EmailIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
  </svg>
);

function TrustItem({ color, path, label }) {
  return (
    <div className="trust-item">
      <div className={`trust-icon ${color}`}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="#fff"><path d={path}/></svg>
      </div>
      {label}
    </div>
  );
}

function ServiceCard({ color, iconPath, title, desc, items, delay = 0 }) {
  const ref = useReveal();
  return (
    <div className={`service-card ${color}`} ref={ref} style={{ transitionDelay: `${delay}s` }}>
      <div className={`service-icon-wrap si-${color}`}>
        <svg viewBox="0 0 24 24" width="26" height="26" fill="#fff"><path d={iconPath}/></svg>
      </div>
      <h3>{title}</h3>
      <p>{desc}</p>
      <ul className="service-list">
        {items.map(i => <li key={i}>{i}</li>)}
      </ul>
    </div>
  );
}

function WhyItem({ num, title, desc, delay = 0 }) {
  const ref = useReveal();
  return (
    <div className="why-item reveal" ref={ref} style={{ transitionDelay: `${delay}s` }}>
      <div className="why-num">{num}</div>
      <div className="why-text"><h4>{title}</h4><p>{desc}</p></div>
    </div>
  );
}

export default function Home() {
  const secHeader1 = useReveal();
  const secHeader2 = useReveal();
  const bilText = useReveal();
  const bilVis = useReveal();
  const ctaText = useReveal();
  const ctaBox = useReveal();
  const { lang } = useLang();
  const tx = t[lang];

  return (
    <main>
      {/* ── HERO ── */}
      <section className="hero">
        <video
          className="hero-bg-video"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="/videos/hero.mp4" type="video/mp4" />
        </video>
        <div className="hero-bg">
          <div className="dots" />
          <div className="circle c1" />
          <div className="circle c2" />
          <div className="circle c3" />
          <div className="diagonal" />
        </div>
        <div className="container">
          <div className="hero-content hero-content-centered">
            <div className="hero-text">
              <div className="eyebrow">{tx.eyebrow}</div>
              <h1>{tx.hero_h1_1}<br />{tx.hero_h1_2} <span className="highlight">{tx.hero_highlight}</span></h1>
              <p>{tx.hero_p}</p>
              <div className="hero-actions">
                <Link to="/contact" className="btn-primary">
                  <EmailIcon /> {tx.hero_cta}
                </Link>
                <a href="tel:7282264437" className="btn-outline">
                  <PhoneIcon /> (728) 226-4437
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ── */}
      <div className="trust-strip">
        <div className="container">
          <div className="trust-inner">
            <TrustItem color="bl" path="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" label={tx.trust1} />
            <TrustItem color="or" path="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm4.24 16L12 15.45 7.77 18l1.12-4.81-3.73-3.23 4.92-.42L12 5l1.92 4.53 4.92.42-3.73 3.23L16.23 18z" label={tx.trust2} />
            <TrustItem color="gr" path="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" label={tx.trust3} />
            <TrustItem color="bl" path="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" label={tx.trust4} />
            <TrustItem color="or" path="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" label={tx.trust5} />
          </div>
        </div>
      </div>

      {/* ── SERVICES PREVIEW ── */}
      <section className="services-section">
        <div className="container">
          <div className="section-header reveal" ref={secHeader1}>
            <span className="tag">{tx.services_tag}</span>
            <h2>{tx.services_h2_1} <span>{tx.services_h2_2}</span></h2>
            <p>{tx.services_p}</p>
          </div>
          <div className="services-grid">
            <ServiceCard color="blue" iconPath="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" title={tx.svc1_title} desc={tx.svc1_desc} items={tx.svc1_items} delay={0} />
            <ServiceCard color="orange" iconPath="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" title={tx.svc2_title} desc={tx.svc2_desc} items={tx.svc2_items} delay={0.1} />
            <ServiceCard color="green" iconPath="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 2-8 2C9 9 8 16 8 16c-3-3-3-8.5-3-8.5S12 6 17 8z" title={tx.svc3_title} desc={tx.svc3_desc} items={tx.svc3_items} delay={0.2} />
          </div>
          <div className="services-cta">
            <Link to="/services" className="btn-primary">{tx.view_all}</Link>
          </div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section className="why-section">
        <div className="container">
          <div className="section-header reveal" ref={secHeader2}>
            <span className="tag" style={{ color: 'var(--orange)' }}>{tx.why_tag}</span>
            <h2 style={{ color: 'var(--white)' }}>{tx.why_h2_1} <span style={{ color: 'var(--orange)' }}>{tx.why_h2_2}</span></h2>
            <p style={{ color: 'rgba(255,255,255,0.65)' }}>{tx.why_p}</p>
          </div>
          <div className="why-grid">
            <WhyItem num="01" title={tx.why1_title} desc={tx.why1_desc} delay={0} />
            <WhyItem num="02" title={tx.why2_title} desc={tx.why2_desc} delay={0.1} />
            <WhyItem num="03" title={tx.why3_title} desc={tx.why3_desc} delay={0.2} />
            <WhyItem num="04" title={tx.why4_title} desc={tx.why4_desc} delay={0.3} />
          </div>
        </div>
      </section>

      {/* ── BILINGUAL ── */}
      <section className="bilingual-section">
        <div className="container">
          <div className="bilingual-grid">
            <div className="bilingual-text reveal" ref={bilText}>
              <span className="tag">{tx.bil_tag}</span>
              <h2>{tx.bil_h2_1} <span>{tx.bil_h2_2}</span></h2>
              <p>{tx.bil_p1}</p>
              <p>{tx.bil_p2}</p>
              <div className="lang-badge-row">
                <span className="lang-badge lb-en">English</span>
                <span className="lang-badge lb-es">Español</span>
              </div>
            </div>
            <div className="bilingual-visual reveal" ref={bilVis} style={{ transitionDelay: '0.15s' }}>
              <div className="bilingual-quote">
                <div className="en">{tx.bil_quote_en}<br />{tx.bil_quote_en2}</div>
                <div className="es">{tx.bil_quote_es}<br />{tx.bil_quote_es2}</div>
              </div>
              <p className="bilingual-sub">– Ángel Fuenmayor, XSC Invest</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-mini-section">
        <div className="container">
          <div className="cta-mini-grid">
            <div className="cta-mini-text reveal" ref={ctaText}>
              <span className="tag">{tx.cta_tag}</span>
              <h2>{tx.cta_h2_1} <span>{tx.cta_h2_2}</span></h2>
              <p>{tx.cta_p}</p>
              <div className="contact-row-item">
                <div className="ci ci-blue">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff"><path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/></svg>
                </div>
                <div><strong>Phone</strong><span>(728) 226-4437</span></div>
              </div>
              <div className="contact-row-item">
                <div className="ci ci-orange">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
                </div>
                <div><strong>Email</strong><span>xscinvest@gmail.com</span></div>
              </div>
            </div>
            <div className="cta-box reveal" ref={ctaBox} style={{ transitionDelay: '0.15s' }}>
              <h3>{tx.cta_box_h3}</h3>
              <p>{tx.cta_box_p}</p>
              <Link to="/contact" className="btn-primary">
                <EmailIcon /> {tx.cta_btn}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
